import type { Field } from './field';
import type { ContentConfig, BehaviorConfig, ThemeConfig, ScoringConfig } from './config';
import type { I18nConfig } from './i18n';
import type { ContentTypeSchema, ContentTypeDefinition, ContentEditProps, ContentPlayerProps } from './content';
import type { ContentGrader, ContentValidator, QuestionCounter } from './grading';
import {
    DEFAULT_EN_STRINGS,
    DEFAULT_VI_STRINGS,
    DEFAULT_ELEMENT_EN_STRINGS,
    DEFAULT_ELEMENT_VI_STRINGS,
    mergeRegisteredElementI18nBundles,
    normalizeContentLocale,
    SUPPORTED_CONTENT_LOCALES,
} from './i18n';

/**
 * Options for defining a content type
 */
export interface DefineContentTypeOptions<TData = unknown, TAnswer = unknown> {
    type: string;
    title: string;
    description?: string;
    version?: string;
    icon?: string;
    category?: ContentTypeSchema['category'];
    fields: Field[];
    defaultData?: TData;
    defaultConfig?: ContentConfig;
    grader?: ContentGrader<TData, TAnswer>;
    validator?: ContentValidator<TData>;
    questionCounter?: QuestionCounter<TData>;
    EditComponent?: React.ComponentType<ContentEditProps<TData>>;
    PlayerComponent?: React.ComponentType<ContentPlayerProps<TData, TAnswer>>;
    /** When true, EditComponent renders its own toggle including Config; ContentEditor skips Content|Config toggle and passes configSlot. */
    hasIntegratedConfigTab?: boolean;
    /** When true, ContentPlayer hides the default nav Submit button. */
    inlinePlayerSubmit?: boolean;
    /** @default true — set false when EditComponent shows `errors` inline */
    showEditorErrorSummary?: boolean;
}

/**
 * Define a content type with schema, grader, and components
 *
 * @example
 * ```ts
 * const mcqType = defineContentType({
 *   type: 'mcq',
 *   title: 'Multiple Choice Question',
 *   version: '1.0.0',
 *   fields: [
 *     { name: 'question', type: 'content-block', label: 'Question' },
 *     { name: 'choices', type: 'list', label: 'Choices', itemField: {...} },
 *   ],
 *   defaultData: { question: '', choices: [] },
 *   grader: { grade: (content, answer) => {...} },
 *   EditComponent: MCQEdit,
 *   PlayerComponent: MCQPlayer,
 * });
 * ```
 */
export function defineContentType<TData = unknown, TAnswer = unknown>(
    options: DefineContentTypeOptions<TData, TAnswer>
): ContentTypeDefinition<TData, TAnswer> {
    const schema: ContentTypeSchema<TData> = {
        type: options.type,
        title: options.title,
        description: options.description,
        version: options.version || '1.0.0',
        icon: options.icon,
        category: options.category,
        fields: options.fields,
        defaultData: options.defaultData,
        defaultConfig: options.defaultConfig ?? getDefaultConfig(),
    };

    return {
        schema,
        grader: options.grader,
        validator: options.validator,
        questionCounter: options.questionCounter,
        EditComponent: options.EditComponent,
        PlayerComponent: options.PlayerComponent,
        hasIntegratedConfigTab: options.hasIntegratedConfigTab,
        inlinePlayerSubmit: options.inlinePlayerSubmit,
        showEditorErrorSummary: options.showEditorErrorSummary,
    };
}

/**
 * Get default content config
 */
export function getDefaultConfig(): ContentConfig {
    return {
        theme: getDefaultTheme(),
        behavior: getDefaultBehavior(),
        i18n: getDefaultI18n(),
        scoring: getDefaultScoring(),
    };
}

/**
 * Get default theme config
 */
export function getDefaultTheme(): ThemeConfig {
    return {
        primaryColor: '#007FA3',
        successColor: '#10B981',
        errorColor: '#EF4444',
        warningColor: '#F59E0B',
        borderRadius: '0.75rem',
    };
}

/**
 * Get default behavior config
 */
export function getDefaultBehavior(): BehaviorConfig {
    return {
        allowRetry: true,
        maxAttempts: 0,
        shuffleChoices: false,
        showCorrectAnswer: true,
        showExplanation: true,
        showHint: true,
        instantFeedback: false,
        examMode: false,
        autoAdvance: false,
    };
}

/**
 * Get default i18n config
 */
export function getDefaultI18n(): I18nConfig {
    const ext = mergeRegisteredElementI18nBundles();
    return {
        defaultLanguage: 'en',
        languages: {
            en: { ...DEFAULT_EN_STRINGS, ...DEFAULT_ELEMENT_EN_STRINGS, ...ext.en },
            vi: { ...DEFAULT_VI_STRINGS, ...DEFAULT_ELEMENT_VI_STRINGS, ...ext.vi },
        },
    };
}

/**
 * Get default scoring config
 */
export function getDefaultScoring(): ScoringConfig {
    return {
        max: 1,
        partialCreditEnabled: false,
        penaltyPerWrongAnswer: 0,
        passingScore: 0.6,
    };
}

/**
 * Merge content configs with defaults
 */
export function mergeConfig(
    defaults: ContentConfig,
    overrides?: Partial<ContentConfig>
): ContentConfig {
    if (!overrides) return defaults;

    return {
        theme: { ...defaults.theme, ...overrides.theme },
        behavior: { ...defaults.behavior, ...overrides.behavior },
        documents: overrides.documents
            ? { ...defaults.documents, ...overrides.documents }
            : defaults.documents,
        i18n: overrides.i18n
            ? (() => {
                  const mergedLangs = {
                      ...defaults.i18n?.languages,
                      ...overrides.i18n.languages,
                  };
                  const languages: Record<string, Record<string, string>> = {};
                  for (const code of SUPPORTED_CONTENT_LOCALES) {
                      languages[code] = {
                          ...(defaults.i18n?.languages?.[code] ?? {}),
                          ...(mergedLangs[code] ?? {}),
                      };
                  }
                  return {
                      defaultLanguage: normalizeContentLocale(
                          overrides.i18n.defaultLanguage ?? defaults.i18n?.defaultLanguage
                      ),
                      languages,
                  };
              })()
            : defaults.i18n,
        scoring: { ...defaults.scoring, ...overrides.scoring },
        timer: overrides.timer ?? defaults.timer,
    };
}

/**
 * Create a content item from schema defaults
 */
export function createContentItem<TData>(
    schema: ContentTypeSchema<TData>,
    id?: string,
    overrides?: Partial<TData>
): { id: string; type: string; data: TData; config: ContentConfig } {
    return {
        id: id ?? generateId(),
        type: schema.type,
        data: { ...schema.defaultData, ...overrides } as TData,
        config: schema.defaultConfig ?? getDefaultConfig(),
    };
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
}
